import React, { useEffect, useMemo, useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import useTranslation from '@/hooks/useTranslation';

const DEFAULT_FORM = {
  fullName: '',
  email: '',
  phone: '',
  positionId: '',
  positionTitle: '',
  locationId: '',
  preferredContactMethod: '',
  availableFrom: '',
  experienceSummary: '',
  coverLetter: '',
};

const TextAreaField = ({ label, value, onChange, placeholder, required = false }) => (
  <div className="space-y-2">
    {label ? (
      <label className="text-sm font-medium text-foreground">
        {label}
        {required ? <span className="text-destructive ml-1">*</span> : null}
      </label>
    ) : null}
    <textarea
      className="min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
    />
  </div>
);

const CreateApplicationModal = ({
  isOpen,
  onClose,
  onSubmit,
  locations = [],
  isSubmitting = false,
}) => {
  const { t } = useTranslation(['hiring', 'common']);
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [error, setError] = useState('');

  const locationOptions = useMemo(
    () =>
      (locations ?? [])
        .filter((location) => location?.id)
        .map((location) => ({ value: location.id, label: location.name ?? location.id })),
    [locations],
  );

  useEffect(() => {
    if (!isOpen) {
      setFormValues(DEFAULT_FORM);
      setError('');
      return;
    }

    setFormValues((prev) => ({
      ...DEFAULT_FORM,
      locationId: prev.locationId || locationOptions[0]?.value || '',
    }));
    setError('');
  }, [isOpen, locationOptions]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (field) => (value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!formValues.fullName.trim() || !formValues.email.trim() || !formValues.positionId.trim()) {
      setError(
        t(
          'hiring.modal.create.validation.required',
          'Please fill in the required fields before submitting.',
        ),
      );
      return;
    }

    try {
      await onSubmit?.({
        ...formValues,
        locationId: formValues.locationId || locationOptions[0]?.value || '',
        positionTitle: formValues.positionTitle || formValues.positionId,
      });
      setFormValues(DEFAULT_FORM);
    } catch (err) {
      const message =
        err?.response?.data?.message
        ?? err?.message
        ?? t('hiring.modal.create.errors.submitFailed', 'Could not save the application.');
      setError(message);
    }
  };

  const disableSubmit =
    !formValues.fullName.trim()
    || !formValues.email.trim()
    || !formValues.positionId.trim()
    || !formValues.locationId.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-elevation-4 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('hiring.modal.create.title', 'Add Application')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'hiring.modal.create.subtitle',
                'Record an application received in person, by phone, or via email.',
              )}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSubmitting}>
            <Icon name="X" size={18} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="grid gap-6 px-6 py-6 md:grid-cols-2">
              <Input
                label={t('hiring.modal.create.fields.fullName', 'Applicant name')}
                required
                value={formValues.fullName}
                onChange={(event) => handleChange('fullName')(event.target.value)}
                placeholder={t('hiring.modal.create.placeholders.fullName', 'Alex Rivera')}
              />

              <Input
                label={t('hiring.modal.create.fields.email', 'Email')}
                type="email"
                required
                value={formValues.email}
                onChange={(event) => handleChange('email')(event.target.value)}
                placeholder="alex@example.com"
              />

              <Input
                label={t('hiring.modal.create.fields.phone', 'Phone (optional)')}
                value={formValues.phone}
                onChange={(event) => handleChange('phone')(event.target.value)}
                placeholder={t('hiring.modal.create.placeholders.phone', '+1 555 123 4567')}
              />

              <Input
                label={t('hiring.modal.create.fields.positionId', 'Position')}
                required
                value={formValues.positionId}
                onChange={(event) => handleChange('positionId')(event.target.value)}
                placeholder={t('hiring.modal.create.placeholders.positionId', 'Line Cook')}
              />

              <Input
                label={t('hiring.modal.create.fields.positionTitle', 'Position title (optional)')}
                value={formValues.positionTitle}
                onChange={(event) => handleChange('positionTitle')(event.target.value)}
                placeholder={t('hiring.modal.create.placeholders.positionTitle', 'Front-of-house Lead')}
              />

              <Select
                label={t('hiring.modal.create.fields.location', 'Location')}
                value={formValues.locationId}
                onChange={handleChange('locationId')}
                options={locationOptions}
                required
                searchable
                placeholder={t('hiring.modal.create.placeholders.location', 'Select location')}
              />

              <Input
                type="date"
                label={t('hiring.modal.create.fields.availableFrom', 'Earliest start date')}
                value={formValues.availableFrom}
                onChange={(event) => handleChange('availableFrom')(event.target.value)}
              />

              <Input
                label={t('hiring.modal.create.fields.preferredContactMethod', 'Preferred contact method')}
                value={formValues.preferredContactMethod}
                onChange={(event) => handleChange('preferredContactMethod')(event.target.value)}
                placeholder={t('hiring.modal.create.placeholders.preferredContactMethod', 'Email or phone')}
              />
            </div>

            <div className="space-y-4 px-6 pb-6">
              <TextAreaField
                label={t('hiring.modal.create.fields.experienceSummary', 'Experience summary')}
                value={formValues.experienceSummary}
                onChange={handleChange('experienceSummary')}
                placeholder={t('hiring.modal.create.placeholders.experienceSummary', 'Relevant experience, specialties, or certifications...')}
              />
              <TextAreaField
                label={t('hiring.modal.create.fields.coverLetter', 'Notes / cover letter')}
                value={formValues.coverLetter}
                onChange={handleChange('coverLetter')}
                placeholder={t('hiring.modal.create.placeholders.coverLetter', 'Why they are interested, interview availability, etc.')}
              />
              {error ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-border bg-muted/30 px-6 py-4">
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {t('common:actions.cancel', 'Cancel')}
            </Button>
            <Button type="submit" variant="default" disabled={disableSubmit || isSubmitting}>
              {isSubmitting
                ? t('hiring.modal.create.actions.submitting', 'Saving...')
                : t('hiring.modal.create.actions.submit', 'Save application')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateApplicationModal;
